import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Card, ItemKind, Member } from '../types';
import { fetchCardsPage, type Queue } from '../lib/api';
import { LOCALE_META } from '../lib/locales';
import { navigate } from '../lib/route';
import type { DecidePayload, useBatch } from '../lib/batch';
import PromptCard, { type CardMode } from '../components/PromptCard';
import BatchButton from '../components/BatchButton';

/* ------------------------------------------------------------ the sentence */

/** What a session of contributing is made of: one choice per slot of
 *  "I'd like to … to … in … from …". Every slot is a URL parameter so the
 *  whole thing is a link. */
export type FocusMode = 'translate' | 'vibe' | 'both';
export type FocusKind = 'questions' | 'ui' | 'store';
export type FocusQueue = 'flagged' | 'unread' | 'open' | 'all';

interface Setup { mode: FocusMode; kind: FocusKind; locale: string; queue: FocusQueue }

const MODES: Record<FocusMode, { label: string; card: CardMode }> = {
  translate: { label: 'check the translations', card: 'translate' },
  vibe: { label: 'rate the cards', card: 'vibe' },
  both: { label: 'translate and rate', card: 'full' },
};
const KINDS: Record<FocusKind, { label: string; db: string }> = {
  questions: { label: 'the questions', db: 'deck' },
  ui: { label: 'the app interface', db: 'ui' },
  store: { label: 'the App Store listing', db: 'store' },
};
const QUEUES: Record<FocusQueue, { label: string; queues: Queue[] }> = {
  open: { label: 'whatever is still open', queues: ['flagged', 'unread'] },
  flagged: { label: 'the known problems', queues: ['flagged'] },
  unread: { label: 'what needs review', queues: ['unread'] },
  all: { label: 'everything, checked or not', queues: ['flagged', 'unread', 'reviewed'] },
};

const LAST = 'focus.last';
const PAGE = 25;
/** Cards kept ready ahead of the one on screen, so the next appears at once. */
const AHEAD = 4;

const isMode = (v: string | null): v is FocusMode => v !== null && v in MODES;
const isKind = (v: string | null): v is FocusKind => v !== null && v in KINDS;
const isQueue = (v: string | null): v is FocusQueue => v !== null && v in QUEUES;

/** Rating is a verdict on a card, and only a question is a card. */
const kindFor = (mode: FocusMode, kind: FocusKind): FocusKind => (mode === 'translate' ? kind : 'questions');

function toUrl(s: Setup) {
  const p = new URLSearchParams({ mode: s.mode, kind: s.kind, locale: s.locale, queue: s.queue });
  return `/contribute?${p}`;
}

function remembered(): Partial<Setup> {
  try { return JSON.parse(localStorage.getItem(LAST) ?? '{}') as Partial<Setup>; } catch { return {}; }
}

export default function Focus({
  member, locales, batch, params,
}: {
  member: Member;
  locales: string[];
  batch: ReturnType<typeof useBatch>;
  params: URLSearchParams;
}) {
  const mode = params.get('mode');
  const kind = params.get('kind');
  const locale = params.get('locale');
  const queue = params.get('queue');
  const complete = isMode(mode) && isKind(kind) && isQueue(queue) && locale && locales.includes(locale);

  if (!complete) {
    return (
      <Lobby
        locales={locales}
        batchCount={batch.items.length}
        // A link to a language you do not have is the one way in here that
        // deserves an explanation rather than a silent default.
        notice={locale && !locales.includes(locale)
          ? `${LOCALE_META[locale]?.name ?? locale} is not one of your languages — pick one that is.`
          : null}
      />
    );
  }
  const setup: Setup = { mode, kind: kindFor(mode, kind), locale, queue };
  return <Stream key={toUrl(setup)} member={member} setup={setup} batch={batch} />;
}

/* ------------------------------------------------------------------ lobby */

function Lobby({
  locales, batchCount, notice,
}: { locales: string[]; batchCount: number; notice: string | null }) {
  const last = remembered();
  const [mode, setMode] = useState<FocusMode>(isMode(last.mode ?? null) ? last.mode! : 'both');
  const [kind, setKind] = useState<FocusKind>(isKind(last.kind ?? null) ? last.kind! : 'questions');
  const [locale, setLocale] = useState(
    last.locale && locales.includes(last.locale) ? last.locale : locales[0]);
  const [queue, setQueue] = useState<FocusQueue>(isQueue(last.queue ?? null) ? last.queue! : 'open');

  const effectiveKind = kindFor(mode, kind);

  const go = () => {
    const s: Setup = { mode, kind: effectiveKind, locale, queue };
    try { localStorage.setItem(LAST, JSON.stringify(s)); } catch { /* a convenience, not a record */ }
    navigate(toUrl(s));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate('/');
      if (e.key === 'Enter' && !(e.target instanceof HTMLSelectElement)) go();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, kind, locale, queue]);

  return (
    <div className="focus lobby">
      <header className="focus-top">
        <button className="btn sm" onClick={() => navigate('/')}>← Back</button>
        <span className="spacer" />
        <BatchButton count={batchCount} onClick={() => navigate('/batch')} />
      </header>

      <main className="lobby-main">
        <p className="lobby-kicker">Contribute</p>
        {/* One sentence, with the choices inside it. Reading it back is the
            whole confirmation step: if it says what you meant, go. */}
        <p className="sentence">
          I’d like to{' '}
          <Slot value={mode} onChange={(v) => setMode(v as FocusMode)} label="what to do"
            options={Object.entries(MODES).map(([k, m]) => [k, m.label])} />
          {' '}for{' '}
          <Slot value={effectiveKind} onChange={(v) => setKind(v as FocusKind)} label="which strings"
            options={Object.entries(KINDS).map(([k, m]) => [k, m.label])}
            disabled={mode !== 'translate'}
            title={mode !== 'translate' ? 'Only a question can be rated, so rating is always about the questions' : undefined} />
          {' '}in{' '}
          <span className="nowrap">
            <Slot value={locale} onChange={setLocale} label="which language"
              options={locales.map((l) => [l, LOCALE_META[l]?.name ?? l])} />,
          </span>
          {' '}working through{' '}
          <span className="nowrap">
            <Slot value={queue} onChange={(v) => setQueue(v as FocusQueue)} label="which queue"
              options={Object.entries(QUEUES).map(([k, m]) => [k, m.label])} />.
          </span>
        </p>

        {notice && <p className="lobby-notice">{notice}</p>}

        <div className="lobby-acts">
          <button className="btn pri lobby-go" onClick={go}>Let’s go <span className="kbd inv">↵</span></button>
          <span className="hint">
            One card at a time, full screen. <b>Enter</b> saves and moves on, <b>0–9</b> rates,
            <b> Esc</b> brings you back here. Everything still lands in your batch first.
          </span>
        </div>
      </main>
    </div>
  );
}

/** A dropdown dressed as a word in the sentence. A select sizes itself to
 *  its widest option, which would leave "Dutch" swimming in a box cut for
 *  "the App Store listing"; the visible word sets the width and the real
 *  control sits over it, invisible but whole. */
function Slot({
  value, onChange, label, options, disabled, title,
}: {
  value: string; onChange: (v: string) => void; label: string;
  options: [string, string][]; disabled?: boolean; title?: string;
}) {
  const text = options.find(([v]) => v === value)?.[1] ?? value;
  return (
    <span className={`slot${disabled ? ' off' : ''}`} title={title}>
      <span className="slot-text" aria-hidden="true">{text}<span className="slot-caret">▾</span></span>
      <select aria-label={label} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </span>
  );
}

/* ----------------------------------------------------------------- stream */

type Step = { card: Card; decided: boolean };

function Stream({
  member, setup, batch,
}: { member: Member; setup: Setup; batch: ReturnType<typeof useBatch> }) {
  const { decisions, decide, undo } = batch;
  const queues = QUEUES[setup.queue].queues;
  const cardMode = MODES[setup.mode].card;

  const [buffer, setBuffer] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  const [showKeys, setShowKeys] = useState(false);
  // What has gone past, newest last. Undo walks it backwards.
  const [history, setHistory] = useState<Step[]>([]);
  // Mirrors of the two lists for the handlers, which must not do their work
  // inside a state updater: StrictMode runs those twice.
  const bufferRef = useRef(buffer); bufferRef.current = buffer;
  const historyRef = useRef(history); historyRef.current = history;

  // Where the next fetch starts. Refs, not state: a fetch in flight must not
  // be re-issued because a render happened.
  const cursor = useRef({ q: 0, offset: 0 });
  const seen = useRef(new Set<string>());
  const pulling = useRef(false);

  const wanted = useCallback((c: Card) => {
    if (decisions.has(c.key)) return false;
    if (setup.mode === 'vibe') return c.vibe?.mine == null;
    // Someone else's change in flight blocks saving, and a card that cannot be
    // saved has no business in a stream meant to be walked without stopping.
    return !c.inflight;
  }, [decisions, setup.mode]);

  const pull = useCallback(async () => {
    if (pulling.current || exhausted) return;
    pulling.current = true;
    setLoading(true);
    try {
      // Keep going until something usable turns up or the queues run dry: a
      // page can be wholly cards already decided, and an empty screen with a
      // spinner is not the same as the end.
      let fresh: Card[] = [];
      while (!fresh.length) {
        const { q, offset } = cursor.current;
        if (q >= queues.length) { setExhausted(true); break; }
        const page = await fetchCardsPage([setup.locale], queues[q], {
          kind: KINDS[setup.kind].db, limit: PAGE, offset,
        });
        cursor.current = page.exhausted ? { q: q + 1, offset: 0 } : { q, offset: offset + PAGE };
        fresh = page.cards.filter((c) => !seen.current.has(c.key) && wanted(c));
        for (const c of fresh) seen.current.add(c.key);
      }
      if (fresh.length) setBuffer((cur) => [...cur, ...fresh]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      pulling.current = false;
      setLoading(false);
    }
  }, [exhausted, queues, setup.locale, setup.kind, wanted]);

  useEffect(() => {
    if (buffer.length <= AHEAD && !exhausted && !loading) void pull();
  }, [buffer.length, exhausted, loading, pull]);

  const current = buffer[0];

  // The card asks to be focused once it has rendered; a new card means a new
  // registration, so the caret lands in it without anyone clicking.
  const focuser = useRef<(() => void) | null>(null);
  const registerFocus = useCallback((_key: string, focus: (() => void) | null) => {
    focuser.current = focus;
    if (focus) requestAnimationFrame(() => focus());
  }, []);

  const advance = useCallback((key: string, decided: boolean) => {
    const cur = bufferRef.current;
    if (cur[0]?.key !== key) return;
    setHistory((h) => [...h, { card: cur[0], decided }]);
    setBuffer(cur.slice(1));
    setDone((n) => n + 1);
  }, []);

  const onDecide = useCallback((card: Card, kind: ItemKind, payload: DecidePayload) => {
    void decide(card, kind, payload);
    // Saving advances from inside the card; a question does not, and in here
    // it should — the point is to keep moving.
    if (kind === 'question') advance(card.key, true);
  }, [decide, advance]);

  const skip = useCallback(() => {
    if (!current) return;
    setHistory((h) => [...h, { card: current, decided: false }]);
    setBuffer((cur) => cur.slice(1));
  }, [current]);

  const back = useCallback(() => {
    const h = historyRef.current;
    const last = h[h.length - 1];
    if (!last) return;
    // A saved card comes out of the batch again; a rated one keeps its score
    // (rating again replaces it) but comes back so it can be. A skipped one
    // was never counted.
    if (last.decided) { void undo(last.card); setDone((n) => Math.max(0, n - 1)); }
    else if (setup.mode === 'vibe' && last.card.vibe?.mine == null) setDone((n) => Math.max(0, n - 1));
    setHistory(h.slice(0, -1));
    setBuffer([last.card, ...bufferRef.current]);
  }, [undo, setup.mode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = Boolean(t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)));
      const cmd = e.metaKey || e.ctrlKey;
      if (e.key === 'Escape') {
        // Out of a textarea first, out of the screen second.
        if (typing && t?.tagName === 'TEXTAREA') { (t as HTMLElement).blur(); return; }
        e.preventDefault(); navigate('/contribute'); return;
      }
      if (cmd && e.key === 'ArrowRight') { e.preventDefault(); skip(); return; }
      if (cmd && e.key === 'ArrowLeft') { e.preventDefault(); back(); return; }
      if (!typing && e.key === '?') { e.preventDefault(); setShowKeys((v) => !v); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [skip, back]);

  const summary = useMemo(() => [
    MODES[setup.mode].label,
    KINDS[setup.kind].label,
    LOCALE_META[setup.locale]?.name ?? setup.locale,
    QUEUES[setup.queue].label,
  ], [setup]);

  const finished = exhausted && !buffer.length && !loading;

  return (
    <div className="focus" data-mode={setup.mode}>
      <header className="focus-top">
        <button className="btn sm" onClick={() => navigate('/contribute')} title="Change what you are contributing (Esc)">
          ← Change
        </button>
        <span className="focus-summary">
          {summary.map((s, i) => <span key={i} className="pill p-mute">{s}</span>)}
        </span>
        <span className="spacer" />
        <span className="focus-tally num" title="Cards you have got through this sitting">
          <b>{done}</b> done
        </span>
        <BatchButton count={batch.items.length} onClick={() => navigate('/batch')} />
      </header>

      <main className="focus-main">
        {error && (
          <div className="panel"><div className="empty">
            <b>Could not load the next card</b>{error}
            <div style={{ marginTop: 12 }}>
              <button className="btn sm" onClick={() => { setError(null); void pull(); }}>Try again</button>
            </div>
          </div></div>
        )}

        {!error && current && (
          <div className="focus-card" key={current.key}>
            <PromptCard
              card={current}
              mode={cardMode}
              decided={decisions.get(current.key)}
              meId={member.id}
              onDecide={onDecide}
              onUndo={(c) => void undo(c)}
              // Notes posted in here are visible in the thread already; the
              // card is not re-fetched, since it is about to move on anyway.
              onNotesChanged={() => {}}
              onRegisterFocus={registerFocus}
              onAdvance={(key) => advance(key, setup.mode !== 'vibe')}
            />
            <div className="focus-under">
              <button className="btn sm" onClick={skip} title="⌘→">
                Skip this one <span className="kbd">⌘→</span>
              </button>
              <button className="btn sm" disabled={!history.length} onClick={back} title="⌘←">
                <span className="kbd">⌘←</span> Back
              </button>
              <span className="spacer" />
              <span className="hint">
                {buffer.length > 1 ? `${buffer.length - 1} more ready` : loading ? 'Fetching more…' : exhausted ? 'Last one' : ''}
              </span>
            </div>
          </div>
        )}

        {!error && !current && !finished && (
          <div className="panel focus-card"><div className="empty">Finding the next card…</div></div>
        )}

        {!error && finished && (
          <div className="panel focus-card"><div className="empty focus-done">
            <b>{done ? `That’s the lot — ${done} card${done === 1 ? '' : 's'} this sitting.` : 'Nothing here to do.'}</b>
            {done
              ? 'Nothing has left the building yet: it is all in your batch, waiting for you to send it.'
              : `Nothing matches that sentence right now. Try another queue or language.`}
            <div className="lobby-acts" style={{ justifyContent: 'center', marginTop: 16 }}>
              {batch.items.length > 0 && (
                <button className="btn pri" onClick={() => navigate('/batch')}>
                  Review and send my batch ({batch.items.length})
                </button>
              )}
              <button className="btn" onClick={() => navigate('/contribute')}>Contribute something else</button>
            </div>
          </div></div>
        )}
      </main>

      <footer className="focus-keys" onClick={() => setShowKeys((v) => !v)} role="note">
        {showKeys ? (
          <>
            <span><span className="kbd">Tab</span> next box</span>
            <span><span className="kbd">↵</span> next box, then save and move on</span>
            <span><span className="kbd">⇧↵</span> line break</span>
            <span><span className="kbd">0–9</span> rate the card</span>
            <span><span className="kbd">⌘→</span> skip</span>
            <span><span className="kbd">⌘←</span> back</span>
            <span><span className="kbd">Esc</span> leave</span>
          </>
        ) : (
          <span>
            {setup.mode === 'vibe'
              ? <><span className="kbd">0–9</span> rate & next</>
              : <><span className="kbd">↵</span> save & next{setup.mode === 'both' && <> · <span className="kbd">0–9</span> rate</>}</>}
            {' '}· <span className="kbd">⌘→</span> skip · <span className="kbd">Esc</span> leave ·{' '}
            <span className="kbd">?</span> all keys
          </span>
        )}
      </footer>
    </div>
  );
}
